import { useState } from 'react';
import type { WorkoutTemplate } from '../../types';
import { Panel, Button } from '../ui';

interface TemplatePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (templateId: string) => Promise<void> | void;
  templates: WorkoutTemplate[];
  title?: string;
}

export function TemplatePicker({
  isOpen,
  onClose,
  onSelect,
  templates,
  title = 'Add template',
}: TemplatePickerProps) {
  const [search, setSearch] = useState('');
  const [selectingTemplateId, setSelectingTemplateId] = useState<string | null>(null);

  // Only show library templates (no clientId = trainer's library)
  const libraryTemplates = templates.filter((t) => !t.clientId);
  const filtered = search.trim()
    ? libraryTemplates.filter((t) =>
        t.name.toLowerCase().includes(search.toLowerCase())
      )
    : libraryTemplates;

  const handleSelect = async (templateId: string) => {
    if (selectingTemplateId) return;

    setSelectingTemplateId(templateId);
    try {
      await onSelect(templateId);
      setSearch('');
      onClose();
    } finally {
      setSelectingTemplateId(null);
    }
  };

  return (
    <Panel isOpen={isOpen} onClose={onClose} title={title} variant="modal" size="sm">
      <div className="space-y-3">
        <input
          className="form-field max-w-none"
          placeholder="Search templates..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />

        {filtered.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6">
            {libraryTemplates.length === 0
              ? 'No library templates yet. Create templates first.'
              : 'No templates match your search.'}
          </p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {filtered.map((t) => (
              <button
                key={t.id}
                onClick={() => handleSelect(t.id)}
                disabled={selectingTemplateId !== null}
                data-testid="template-picker-option"
                data-template-id={t.id}
                className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors disabled:opacity-60 disabled:cursor-wait"
              >
                <p className="font-medium text-gray-900 dark:text-white">{t.name}</p>
                <p className="text-xs text-slate-400">
                  {selectingTemplateId === t.id
                    ? 'Adding...'
                    : `${t.items?.length || 0} exercise${(t.items?.length || 0) !== 1 ? 's' : ''}`}
                </p>
              </button>
            ))}
          </div>
        )}

        <Button variant="ghost" size="sm" onClick={onClose} className="w-full">
          Cancel
        </Button>
      </div>
    </Panel>
  );
}
